import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQuery } from './baseQuery';
import { IPagedResult } from '../../shared/models/paged-result';
import {
  IOrganization,
  IOrganizationApiKey,
  IOrganizationApiKeySecretResponse,
  IOrganizationApiKeyUpsert,
} from '../../shared/models/core/organization';
import { OrganizationFilterSet } from '../../shared/models/core/organization-filter-set';
import { serialize } from '../../utility/serialize';

export const organizationApi = createApi({
  reducerPath: 'organizationApi',
  baseQuery,
  tagTypes: ['Organizations', 'OrganizationApiKeys'],
  endpoints: (builder) => ({
    getOrganizations: builder.query<IPagedResult<IOrganization>, OrganizationFilterSet | void>({
      query: (filterSet) => {
        const qs = filterSet ? `?${serialize(filterSet)}` : '';
        return {
          url: `/api/organizations${qs}`,
          method: 'GET',
        };
      },
      providesTags: ['Organizations'],
    }),
    getOrganization: builder.query<IOrganization, string>({
      query: (id) => ({
        url: `/api/organizations/${id}`,
        method: 'GET',
      }),
      providesTags: (_result, _error, id) => [{ type: 'Organizations', id }],
    }),
    createOrganization: builder.mutation<string, Partial<IOrganization>>({
      query: (body) => ({
        url: '/api/organizations',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Organizations'],
    }),
    updateOrganization: builder.mutation<void, Partial<IOrganization> & { id: string }>({
      query: ({ id, ...body }) => ({
        url: `/api/organizations/${id}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        'Organizations',
        { type: 'Organizations', id },
      ],
    }),
    deleteOrganization: builder.mutation<void, string>({
      query: (id) => ({
        url: `/api/organizations/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Organizations'],
    }),
    getOrganizationApiKeys: builder.query<IOrganizationApiKey[], string>({
      query: (organizationId) => ({
        url: `/api/organizations/${organizationId}/apikeys`,
        method: 'GET',
      }),
      providesTags: (_result, _error, organizationId) => [
        'OrganizationApiKeys',
        { type: 'Organizations', id: organizationId },
      ],
    }),
    createOrganizationApiKey: builder.mutation<
      IOrganizationApiKeySecretResponse,
      { organizationId: string; body: IOrganizationApiKeyUpsert }
    >({
      query: ({ organizationId, body }) => ({
        url: `/api/organizations/${organizationId}/apikeys`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_result, _error, { organizationId }) => [
        'OrganizationApiKeys',
        { type: 'Organizations', id: organizationId },
      ],
    }),
    updateOrganizationApiKey: builder.mutation<
      IOrganizationApiKey,
      { organizationId: string; apiKeyId: string; body: IOrganizationApiKeyUpsert }
    >({
      query: ({ organizationId, apiKeyId, body }) => ({
        url: `/api/organizations/${organizationId}/apikeys/${apiKeyId}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: (_result, _error, { organizationId }) => [
        'OrganizationApiKeys',
        { type: 'Organizations', id: organizationId },
      ],
    }),
    renewOrganizationApiKey: builder.mutation<
      IOrganizationApiKeySecretResponse,
      { organizationId: string; apiKeyId: string; expiresAt: string }
    >({
      query: ({ organizationId, apiKeyId, expiresAt }) => ({
        url: `/api/organizations/${organizationId}/apikeys/${apiKeyId}/renew`,
        method: 'POST',
        body: { expiresAt },
      }),
      invalidatesTags: (_result, _error, { organizationId }) => [
        'OrganizationApiKeys',
        { type: 'Organizations', id: organizationId },
      ],
    }),
    revokeOrganizationApiKey: builder.mutation<void, { organizationId: string; apiKeyId: string }>({
      query: ({ organizationId, apiKeyId }) => ({
        url: `/api/organizations/${organizationId}/apikeys/${apiKeyId}/revoke`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, { organizationId }) => [
        'OrganizationApiKeys',
        { type: 'Organizations', id: organizationId },
      ],
    }),
  }),
});

export const {
  useGetOrganizationsQuery,
  useLazyGetOrganizationsQuery,
  useGetOrganizationQuery,
  useCreateOrganizationMutation,
  useUpdateOrganizationMutation,
  useDeleteOrganizationMutation,
  useGetOrganizationApiKeysQuery,
  useCreateOrganizationApiKeyMutation,
  useUpdateOrganizationApiKeyMutation,
  useRenewOrganizationApiKeyMutation,
  useRevokeOrganizationApiKeyMutation,
} = organizationApi;
